/**
 * デバッグIssueのMarkdown生成
 */

import {
  PROBLEM_TYPE_LABELS as problemLabels,
  OCCURRENCE_RATE_LABELS as occurrenceLabels,
  AI_FEATURE_LABELS as aiFeatureLabels,
} from './types';

import type {
  DebugIssueData,
  ProblemType,
  OccurrenceRate,
} from './types';


/**
 * 問題タイプをチェックボックス形式に変換
 */
function formatProblemTypes(selected: ProblemType[]): string {
  const allTypes: ProblemType[] = [
    'error_display',
    'wrong_suggestion_type',
    'wrong_suggestion_content',
    'button_not_working',
    'ui_broken',
    'timeout',
    'other',
  ];

  return allTypes
    .map((type) => {
      const checked = selected.includes(type) ? 'x' : ' ';
      return `- [${checked}] ${problemLabels[type]}`;
    })
    .join('\n');
}

/**
 * 発生率をチェックボックス形式に変換
 */
function formatOccurrenceRate(selected: OccurrenceRate): string {
  const allRates: OccurrenceRate[] = ['reproducible', 'half', 'rare'];

  return allRates
    .map((rate) => {
      const checked = selected === rate ? 'x' : ' ';
      return `- [${checked}] ${occurrenceLabels[rate]}`;
    })
    .join('\n');
}

/**
 * チャット履歴をフォーマット
 */
function formatChatHistory(
  history: DebugIssueData['autoCollected']['chatHistory']
): string {
  if (history.length === 0) {
    return '(アクション履歴なし)';
  }

  return history
    .map((msg) => {
      const time = new Date(msg.timestamp).toLocaleTimeString('ja-JP', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      const role = msg.role === 'user' ? 'User' : 'Assistant';
      // 長いメッセージは省略
      const content = msg.content.length > 500
        ? msg.content.slice(0, 500) + '...(省略)'
        : msg.content;
      return `[${time}] ${role}: ${content}`;
    })
    .join('\n');
}

/**
 * コンソールエラーをフォーマット
 */
function formatConsoleErrors(
  errors: DebugIssueData['autoCollected']['consoleErrors']
): string {
  if (errors.length === 0) {
    return '(エラーなし)';
  }

  return errors
    .map((err) => {
      const time = new Date(err.timestamp).toLocaleTimeString('ja-JP', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      const stackInfo = err.stack ? `\n    ${err.stack.split('\n').slice(0, 3).join('\n    ')}` : '';
      return `[${time}] ${err.level.toUpperCase()}: ${err.message}${stackInfo}`;
    })
    .join('\n\n');
}

/**
 * 環境ラベルを取得
 */
function getEnvironmentLabel(env: DebugIssueData['autoCollected']['environment']): string {
  const labels: Record<typeof env, string> = {
    production: '本番 (kakusill.atsumell.com)',
    staging: 'staging',
    sandbox: 'sandbox',
    development: 'localhost',
    unknown: 'unknown',
  };
  return labels[env];
}

/**
 * DebugIssueDataからMarkdownを生成
 */
export function formatIssueBody(data: DebugIssueData): string {
  const { userInput, autoCollected, screenshot } = data;

  // 日時フォーマット
  const timestamp = new Date(autoCollected.timestamp);
  const formattedDate = timestamp.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'Asia/Tokyo',
  });

  // 選択ソースのフォーマット
  const sourcesText = autoCollected.selectedSources
    .map((s) => {
      if (s.type === 'none') return '- [x] ソース選択なし';
      if (s.type === 'pdf') return `- [x] PDFを選択: ${s.name || s.id || '(不明)'}`;
      return `- [x] ページを選択: ${s.name || s.id || '(不明)'}`;
    })
    .join('\n');

  return `## 問題の種類

${formatProblemTypes(userInput.problemTypes)}

## 問題の詳細

${userInput.problemDetail || '(未入力)'}

## 期待した動作

${userInput.expectedBehavior || '(未入力)'}

## 実際の動作

${userInput.actualBehavior || '(未入力)'}

## 発生率

${formatOccurrenceRate(userInput.occurrenceRate)}

## 補足情報

${userInput.additionalInfo || '(なし)'}

---

## 自動収集データ

> 以下はデバッグツールにより自動収集されました

### 基本情報

| 項目 | 値 |
|-----|-----|
| 発生日時 | ${formattedDate} JST |
| URL | ${autoCollected.url} |
| 環境 | ${getEnvironmentLabel(autoCollected.environment)} |
| ブラウザ | ${autoCollected.browser} |
| 画面サイズ | ${autoCollected.screenSize.width} x ${autoCollected.screenSize.height} |
| ビューポート | ${autoCollected.viewportSize.width} x ${autoCollected.viewportSize.height} |

### 使用したAI機能

- ${aiFeatureLabels[autoCollected.aiFeature]}

### 選択ソース

${sourcesText}

### ユーザー入力内容（最新）

\`\`\`
${autoCollected.lastUserInput || '(入力なし)'}
\`\`\`

### アクション履歴

<details>
<summary>クリックして展開（${autoCollected.chatHistory.length}件）</summary>

\`\`\`
${formatChatHistory(autoCollected.chatHistory)}
\`\`\`

</details>

### アプリ状態

| 項目 | 値 |
|-----|-----|
| 選択ページ | ${autoCollected.appState.selectedPageTitle || '(なし)'} (\`${autoCollected.appState.selectedPageId || 'null'}\`) |
| AI生成中 | ${autoCollected.appState.isAIGenerating ? 'はい' : 'いいえ'} |
| WebSocket状態 | ${autoCollected.appState.wsStatus === undefined ? '不明' : autoCollected.appState.wsStatus ? '接続中' : '切断'} ${autoCollected.appState.wsProgress !== undefined ? `(${autoCollected.appState.wsProgress}%)` : ''} |
| 編集状態 | ${autoCollected.appState.isDirty ? '未保存の変更あり' : '保存済み'} |
| 選択コンテキスト | ${autoCollected.appState.selectionContext || 'general'} |

### コンソールエラー（直近）

<details>
<summary>クリックして展開（${autoCollected.consoleErrors.length}件）</summary>

\`\`\`
${formatConsoleErrors(autoCollected.consoleErrors)}
\`\`\`

</details>

### スクリーンショット

${screenshot?.uploadedUrl ? `![screenshot](${screenshot.uploadedUrl})` : '(スクリーンショットはIssue作成後に手動で添付してください)'}

---
*Generated by Debug Issue Tool*
`;
}

/**
 * Issue作成用のタイトルを生成
 */
export function formatIssueTitle(userInput: DebugIssueData['userInput']): string {
  return userInput.title.trim();
}

/**
 * 簡素化バグ報告からMarkdownを生成（AI 整形なしのフォールバック用）
 */
export function formatSimplifiedIssueBody(input: {
  complaint: string;
  expectedBehavior?: string;
  stepsToReproduce?: string;
  environmentInfo?: string;
  autoCollected?: DebugIssueData['autoCollected'];
  screenshot?: DebugIssueData['screenshot'];
}): string {
  const sections: string[] = [];

  sections.push(`## 事象\n\n${input.complaint}`);

  if (input.expectedBehavior?.trim()) {
    sections.push(`## 期待される動作\n\n${input.expectedBehavior}`);
  }

  if (input.stepsToReproduce?.trim()) {
    sections.push(`## 再現手順\n\n${input.stepsToReproduce}`);
  }

  if (input.environmentInfo?.trim()) {
    sections.push(`## 環境情報\n\n${input.environmentInfo}`);
  }

  if (input.autoCollected) {
    const ac = input.autoCollected;
    const timestamp = new Date(ac.timestamp);
    const formattedDate = timestamp.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Asia/Tokyo',
    });

    sections.push(`---\n\n## 自動収集データ\n\n> 以下はデバッグツールにより自動収集されました\n\n| 項目 | 値 |\n|-----|-----|\n| 発生日時 | ${formattedDate} JST |\n| URL | ${ac.url} |\n| ブラウザ | ${ac.browser} |\n| 画面サイズ | ${ac.screenSize.width} x ${ac.screenSize.height} |`);
  }

  if (input.screenshot?.uploadedUrl) {
    sections.push(`### スクリーンショット\n\n![screenshot](${input.screenshot.uploadedUrl})`);
  }

  sections.push('---\n*Generated by Debug Issue Tool*');

  return sections.join('\n\n');
}

/**
 * GitHub Issue作成URLを生成
 *
 * ブラウザのURL長制限（~8KB）を考慮し、bodyが長すぎる場合は切り詰める
 * GitHub自体も約4KB程度の制限があるため、安全マージンを取る
 */
export function generateGitHubIssueUrl(
  owner: string,
  repo: string,
  title: string,
  body: string,
  labels: string[] = ['waiting-for-triage']
): string {
  // URL長制限を考慮（GitHub制限 + URLエンコード膨張を考慮して3000文字）
  const MAX_BODY_LENGTH = 3000;
  let truncatedBody = body;

  if (body.length > MAX_BODY_LENGTH) {
    truncatedBody = body.slice(0, MAX_BODY_LENGTH) +
      '\n\n---\n⚠️ *内容が長すぎるため切り詰められました。詳細はスクリーンショットを参照してください。*';
  }

  const params = new URLSearchParams({
    title,
    body: truncatedBody,
    labels: labels.join(','),
  });

  return `https://github.com/${owner}/${repo}/issues/new?${params.toString()}`;
}
